import { createRouter, createWebHistory } from 'vue-router'
import Auth                               from '@/composables/Auth'
import CallbackView                       from '@/views/CallbackView.vue'
import { supabase }                       from '@/supabase'
import { nextTick }                       from 'vue'


// A guest (anonymous Supabase session) counts as logged out for most guards -
// they must be able to reach /login and /signup to become a full account.
const { auth_guard, is_authenticated, is_full_account, user, agreed_to_terms, has_been_processed, email_verification_guard } = Auth()

const shop_usage_auth_guard = (to, from, next) => {
  if (!is_full_account.value) {
    next()
  }
  else {
    router.push('/shop_usage/accept_terms')
  }
}

const logged_out_auth_guard = (to, from, next) => {
  if (!is_full_account.value) {
    next()
  }
  else {
    router.push('/')
  }
}

// Guest funnel. The landing page's one job is the first click that mints an
// anonymous session - a full account already has one, so it starts at the
// location step, where it picks one of its homes or adds a new address.
const guest_entry_guard = (to, from, next) => {
  if (is_full_account.value) {
    next({ path: '/guest/location' })
  }
  else {
    next()
  }
}

// The steps themselves take any session, guest or full account; a visitor with
// no session at all starts at the front door.
const guest_guard = (to, from, next) => {
  if (!is_authenticated.value) {
    next({ path: '/guest' })
  }
  else {
    next()
  }
}

// Adding an address is the signed-in account's page; a guest types its address
// on the location step itself, alongside the email we need from it.
const guest_account_guard = (to, from, next) => {
  if (!is_authenticated.value) {
    next({ path: '/guest' })
  }
  else if (!is_full_account.value) {
    next({ path: '/guest/location' })
  }
  else {
    next()
  }
}

// Conversion turns a guest into an account - a full account has nothing to
// convert and goes home instead.
const guest_convert_guard = (to, from, next) => {
  if (is_full_account.value) {
    next({ path: '/' })
  }
  else {
    guest_guard(to, from, next)
  }
}

const renewal_auth_guard = (to, from, next) => {
  next()
}

const check_for_auth_guard = async (to, from, next) => {

  // On the OAuth callback the code exchange owns the session. Rehydrating
  // from cookies here races it - and during guest-to-account conversion the
  // cookies still hold the ANONYMOUS session's tokens, so the rehydrate
  // resurrects the guest session right over the fresh Google login.
  if (to.path === '/callback') {
    return next()
  }

  const cookies = document.cookie.split(/\s*;\s*/).map(cookie => cookie.split('='))
  const accessTokenCookie = cookies.find(x => x[0] === 'my-access-token')
  const refreshTokenCookie = cookies.find(x => x[0] === 'my-refresh-token')
  const logged_out_token = cookies.find(x => x[0] === 'logged_out_token')

  if (!is_authenticated.value) {
    if (accessTokenCookie && refreshTokenCookie) {
      if (logged_out_token && accessTokenCookie) {
        if (accessTokenCookie[1] !== logged_out_token[1]) {
          await supabase.auth.setSession({
            access_token: accessTokenCookie[1],
            refresh_token: refreshTokenCookie[1]
          })
        }
      }
      else {
        await supabase.auth.setSession({
          access_token: accessTokenCookie[1],
          refresh_token: refreshTokenCookie[1]
        })
      }
    }
  }
  else {
    if (logged_out_token && accessTokenCookie && accessTokenCookie[1] && logged_out_token[1]) {
      if (accessTokenCookie[1] === logged_out_token[1]) {
        localStorage.removeItem('sb-login-auth-token')
        const expires = new Date(0).toUTCString()
        document.cookie = `logged_out_token=; Domain=${
          import.meta.env.VITE_AUTH_DOMAIN
        }; path=/; expires=${expires}; SameSite=Lax; secure`
        document.cookie = `my-access-token=; Domain=${
          import.meta.env.VITE_AUTH_DOMAIN
        }; path=/; expires=${expires}; SameSite=Lax; secure`
        document.cookie = `my-refresh-token=; Domain=${
          import.meta.env.VITE_AUTH_DOMAIN
        }; path=/; expires=${expires}; SameSite=Lax; secure`
        window.location.reload()
      }
    }
  }

  next()
}

const router = createRouter({
  history: createWebHistory(),
  scrollBehavior(to, from, savedPosition) {
    if (savedPosition) {
      return savedPosition
    }
    else if (to.hash) {
      return new Promise((resolve, reject) => {
        setTimeout(() => {
          resolve({ el: to.hash, top: 70, behavior: 'smooth' })
        }, 500)
      })
    }
    else {
      return { top: 0 }
    }
  },
  routes: [
    {
      path: '/guest',
      component: () => import('@/layouts/GuestLayout.vue'),
      children: [
        {
          path: '',
          name: 'guest_start',
          component: () => import('@/views/guest/GuestStartView.vue'),
          beforeEnter: guest_entry_guard,
          meta: { title: 'Find Your Best Plan' }
        },
        {
          path: 'location',
          name: 'guest_location',
          component: () => import('@/views/guest/GuestLocationView.vue'),
          beforeEnter: guest_guard,
          meta: { title: 'Find Your Home' }
        },
        {
          path: 'location/new',
          name: 'guest_location_new',
          component: () => import('@/views/guest/GuestLocationNew.vue'),
          beforeEnter: guest_account_guard,
          meta: { title: 'Add a New Address' }
        },
        {
          path: 'usage',
          name: 'guest_usage',
          component: () => import('@/views/guest/GuestUsageView.vue'),
          beforeEnter: guest_guard,
          meta: { title: 'Your Real Usage' }
        },
        {
          path: 'plans',
          name: 'guest_plans',
          component: () => import('@/views/guest/GuestPlansView.vue'),
          beforeEnter: guest_guard,
          meta: { title: 'Your Best Plans' }
        },
        {
          path: 'convert',
          name: 'guest_convert',
          component: () => import('@/views/guest/GuestConvertView.vue'),
          beforeEnter: guest_convert_guard,
          meta: { title: 'Save Your Account' }
        }
      ]
    },
    {
      // path: '/notifications',
      // component: () => import('@/layouts/MainLayout.vue'),
      // children: {
      path: '/opt_out',
      component: () => import('@/views/notifications/OptOut.vue'),
      props: route => ({ ...route.query }),
      meta: { title: 'Unsubscribe' }
      // }
    },
    {

      path: '/shop_usage',
      component: () => import('@/layouts/ShopUsageAltLayout.vue'),
      children: [
        {
          path: '',
          name: 'shop_usage_default',
          component: () => import('@/views/shop_usage/SignUpView.vue'),
          beforeEnter: shop_usage_auth_guard,
          meta: { title: 'Sign Up' }
        },
        {
          path: 'signup',
          name: 'shop_usage_signup',
          component: () => import('@/views/shop_usage/SignUpView.vue'),
          beforeEnter: shop_usage_auth_guard,
          meta: { title: 'Sign Up' }
        },
        {
          path: 'login',
          name: 'shop_usage_login',
          component: () => import('@/views/shop_usage/LoginView.vue'),
          beforeEnter: shop_usage_auth_guard,
          meta: { title: 'Login' }
        }
      ]
    },
    {
      path: '/shop_usage',
      component: () => import('@/layouts/ShopUsageLayout.vue'),
      children: [

        {
          path: 'password/reset',
          name: 'shop_usage_password_reset',
          component: () => import('@/views/shop_usage/PasswordResetView.vue'),
          meta: { title: 'Password Reset' },
          props: route => ({ email_address: route.query.email_address })
        },
        {
          path: 'verify',
          name: 'shop_usage_verify',
          component: () => import('@/views/shop_usage/AccountVerificationView.vue'),
          beforeEnter: shop_usage_auth_guard,
          meta: { title: 'Verify your account' }
        },
        {
          path: 'accept_terms',
          name: 'shop_usage_accept_terms',
          component: () => import('@/views/shop_usage/AcceptTermsView.vue'),
          beforeEnter: (to, from, next) => {
            if (is_authenticated.value) {
              if (agreed_to_terms.value) {
                router.push('/shop_usage/process')
              }
              else {
                next()
              }
            }
            else {
              router.push('/shop_usage/login')
            }
          },
          meta: { title: 'Accept Terms and Conditions' }
        },
        {
          path: 'process',
          name: 'shop_usage_process',
          component: () => import('@/views/shop_usage/LoadLocationFromOrdersView.vue'),
          beforeEnter: async (to, from, next) => {
            if (is_authenticated.value) {
              if (user.value.user_metadata.processed_orders) {
                next({ path: '/shop_usage/select_location' })
              }
              else {
                next()
              }
            }
            else {
              next({ path: '/shop_usage/login' })
            }
          },
          meta: { title: 'Load locations from orders' }
        },
        {
          path: 'select_location',
          name: 'shop_usage_select_location',
          component: () => import('@/views/shop_usage/SelectLocationView.vue'),
          beforeEnter: async (to, from, next) => {
            if (is_authenticated.value) {
              next()
            }
            else {
              next({ path: '/shop_usage/login' })
            }
          },
          meta: { title: 'Load locations from orders' }
        },
        {
          path: 'add_location',
          name: 'shop_usage_add_location',
          component: () => import('@/views/shop_usage/AddServiceLocationView.vue'),
          beforeEnter: async (to, from, next) => {
            if (is_authenticated.value) {
              next()
            }
            else {
              next({ path: '/shop_usage/login' })
            }
          },
          meta: { title: 'Add new location' }
        },
        {
          path: 'live_link',
          name: 'enable_live_link',
          component: () => import('@/views/shop_usage/EnableLiveLinkView.vue'),
          beforeEnter: async (to, from, next) => {
            if (is_authenticated.value) {
              next()
            }
            else {
              next({ path: '/shop_usage/login' })
            }
          },
          meta: { title: 'Enable Live Link&trade;' }
        },
        {
          path: 'summary/:id',
          name: 'shop_usage_summary',
          component: () => import('@/views/shop_usage/SummaryView.vue'),
          beforeEnter: async (to, from, next) => {
            if (is_authenticated.value) {
              next()
            }
            else {
              next({ path: '/shop_usage/login' })
            }
          },
          meta: { title: 'Location Summary' },
          props: true
        }
      ]
    },

    {
      path: '/renewal',
      component: () => import('@/layouts/ShopUsageLayout.vue'),
      children: [
        {
          path: '',
          name: 'renewal_default',
          component: () => import('@/views/renewal/SignUpView.vue'),
          beforeEnter: renewal_auth_guard,
          meta: { title: 'Sign Up' }
        },
        {
          path: 'login',
          name: 'renewal_login',
          component: () => import('@/views/renewal/LoginView.vue'),
          beforeEnter: renewal_auth_guard,
          meta: { title: 'Login' }
        },
        {
          path: 'signup',
          name: 'renewal_signup',
          component: () => import('@/views/renewal/SignUpView.vue'),
          beforeEnter: renewal_auth_guard,
          meta: { title: 'Sign Up' }
        },
        {
          path: 'password/reset',
          name: 'renewal_password_reset',
          component: () => import('@/views/renewal/PasswordResetView.vue'),
          meta: { title: 'Password Reset' },
          props: route => ({ email_address: route.query.email_address })
        },
        {
          path: 'account/verify',
          name: 'renewal_verify',
          component: () => import('@/views/renewal/AccountVerificationView.vue'),
          beforeEnter: renewal_auth_guard,
          props: route => ({ email: route.query.email }),
          meta: { title: 'Verify your account' }
        },
        {
          path: 'accept_terms',
          name: 'renewal_accept_terms',
          component: () => import('@/views/renewal/AcceptTermsView.vue'),
          beforeEnter: (to, from, next) => {
            if (is_authenticated.value) {
              if (agreed_to_terms.value) {
                router.push('/renewal/process')
              }
              else {
                next()
              }
            }
            else {
              router.push('/renewal/login')
            }
          },
          meta: { title: 'Accept Terms and Conditions' }
        },
        {
          path: 'process',
          name: 'renewal_process',
          component: () => import('@/views/renewal/ProcessView.vue'),
          beforeEnter: async (to, from, next) => {
            if (is_authenticated.value) {
              if (agreed_to_terms.value) {
                if (!has_been_processed.value) {
                  next()
                }
                else {
                  router.push('/')
                }
              }
              else {
                router.push('/renewal/accept_terms')
              }
            }
            else {
              router.push('/renewal/login')
            }

          },
          meta: { title: 'Load locations from orders' }
        }
      ]
    },

    {
      path: '/',
      component: () => import('@/layouts/MainLayout.vue'),
      beforeEnter: auth_guard,
      children: [
        {
          path: '',
          name: 'home',
          component: () => import('@/views/HomeView.vue'),
          meta: { title: 'Dashboard' }
        },

        // Orders
        {
          path: 'orders',
          name: 'Orders',
          component: () => import('@/views/orders/MyOrders.vue'),
          beforeEnter: auth_guard,
          meta: { title: 'My Orders' }
        },
        {
          path: 'orders/:id',
          name: 'Order Summary',
          component: () => import('@/views/orders/OrderSummary.vue'),
          meta: { title: 'Order Summary' },
          props: true
        },
        {
          path: 'orders/:id/renewal_opt_out',
          name: 'Order Renewal Summary',
          component: () => import('@/views/orders/OrderSummary.vue'),
          beforeEnter: auth_guard,
          meta: { title: 'Order Renewal Summary' },
          props: r => ({ id: r.params.id, show_renewal_opt_out: true })
        },

        // Service Locations
        {
          path: 'locations',
          name: 'Locations',
          component: () => import('@/views/service_locations/MyServiceLocations.vue'),
          meta: { title: 'My Locations' },
          props: true
        },
        {
          path: 'locations/:id',
          name: 'Location Summary',
          component: () => import('@/views/service_locations/ServiceLocationView.vue'),
          meta: { title: 'Location Summary' },
          props: true
        },
        {
          path: 'locations/:id/plans',
          name: 'Location Best Plans',
          component: () => import('@/views/service_locations/ServiceLocationPlansView.vue'),
          meta: { title: 'Best Plans' },
          props: true
        },
        {
          path: 'locations/:id/connection_manager',
          name: 'Connection Manager',
          component: () => import('@/views/service_locations/ConnectionManager.vue'),
          meta: { title: 'Connection Manager' },
          props: true
        },
        {
          path: 'locations/add',
          name: 'Add Service Location',
          component: () => import('@/views/service_locations/AddServiceLocation.vue'),
          meta: { title: 'Add Service Location' }
        },
        {
          path: 'shop',
          name: 'shop',
          component: () => import('@/views/ShopView.vue'),
          meta: { title: 'Shop' }
        },

        // User Section
        {
          path: 'account',
          name: 'My Account',
          component: () => import('@/views/user/MyAccount.vue'),
          meta: { title: 'My Account' }
        },

        // Notifications
        {
          path: 'notifications',
          name: 'Notifications',
          component: () => import('@/views/notifications/NotificationsView.vue'),
          meta: { title: 'Notifications' }
        },

        // Affiliate
        {
          path: 'affiliates',
          name: 'Affiliates',
          component: () => import('@/views/affiliates/Hub.vue'),
          meta: { title: 'Affiliates' }
        }
      ]
    },

    // Auth
    {
      path: '/',
      component: () => import('@/layouts/AuthLayout.vue'),
      children: [
        {
          path: 'login',
          name: 'login',
          component: () => import('@/views/auth/LoginView.vue'),
          beforeEnter: logged_out_auth_guard,
          meta: { title: 'Log In' }
        },
        {
          path: 'password/reset',
          name: 'password_reset',
          component: () => import('@/views/auth/PasswordResetView.vue'),
          beforeEnter: logged_out_auth_guard,
          meta: { title: 'Password Reset' },
          props: route => ({ email_address: route.query.email_address })
        },
        {
          path: '/affiliates/password',
          name: 'Affiliates - Set Password',
          component: () => import('@/views/affiliates/SetPasswordView.vue'),
          beforeEnter: auth_guard,
          meta: { title: 'Affiliates - Set Password' }
        },
        {
          path: '/affiliates/expired_invite_link',
          name: 'Affiliates - Expired Invite Link',
          component: () => import('@/views/affiliates/ExpiredInviteLink.vue'),
          beforeEnter: logged_out_auth_guard,
          meta: { title: 'Affiliates - Expired Invite Link' }
        },
        {
          path: 'account/verify',
          name: 'account_verification',
          component: () => import('@/views/auth/AccountVerificationView.vue'),
          beforeEnter: email_verification_guard,
          meta: { title: 'Account Verification' },
          props: route => ({ email: route.query.email })
        },
        {
          path: 'signup',
          name: 'signup',
          component: () => import('@/views/auth/SignUpView.vue'),
          beforeEnter: logged_out_auth_guard,
          meta: { title: 'Sign Up' }
        },
        {
          path: 'accept_terms',
          name: 'accept_terms',
          component: () => import('@/views/auth/AcceptTermsView.vue'),
          beforeEnter: (to, from, next) => {
            if (is_authenticated.value) {
              if (agreed_to_terms.value) {
                router.push('/')
              }
              else {
                next()
              }
            }
            else {
              router.push('/login')
            }
          },
          meta: { title: 'Accept Terms and Conditions' }
        },
        {
          path: 'process',
          name: 'process',
          component: () => import('@/views/auth/ProcessView.vue'),
          beforeEnter: (to, from, next) => {
            if (is_authenticated.value) {
              if (agreed_to_terms.value) {
                if (!has_been_processed.value) {
                  next()
                }
                else {
                  router.push('/')
                }
              }
              else {
                router.push('/accept_terms')
              }
            }
            else {
              router.push('/login')
            }
          },
          meta: { title: 'Accept Terms and Conditions' }
        },
        {
          path: 'add_location',
          name: 'add_location',
          component: () => import('@/views/auth/AddServiceLocation.vue'),
          beforeEnter: (to, from, next) => {
            if (is_authenticated.value) {
              if (agreed_to_terms.value) {
                if (has_been_processed.value) {
                  next()
                }
                else {
                  router.push('/process')
                }
              }
              else {
                router.push('/accept_terms')
              }
            }
            else {
              router.push('/login')
            }
          },
          meta: { title: 'Accept Terms and Conditions' }
        }
      ]
    },

    {
      path: '/callback',
      name: 'callback',
      component: CallbackView,
      meta: { title: 'callback' }
    },

    // Catch all
    {
      path: '/:catchAll(.*)*',
      component: () => import('@/views/ErrorNotFound.vue')
    }
  ],
  linkActiveClass: 'active'
})

router.beforeEach(check_for_auth_guard)

const DEFAULT_TITLE = 'ComparePower Portal'
router.afterEach((to, from) => {
  nextTick(() => {
    document.title = to.meta.title || DEFAULT_TITLE
  })
})
export default router
